import {useRouter} from 'next/router';
import {useCallback, useEffect, useMemo, useState} from 'react';

import useUserActions from '@/src/app/store/user/useUserActions';
import {getAuthorizationToken} from '@/src/app/utils/authorizationToken';

import Timer from '../components/Timer';
import {getTimerDate} from '../utils/getTimerDate';

type TimerContainerProps = {
  isRounded?: boolean;
  onClose?: () => void;
};

const TimerContainer = ({isRounded, onClose}: TimerContainerProps) => {
  const {locale} = useRouter();
  const {user: userData} = useUserActions();

  const authorizationToken = getAuthorizationToken();

  const [endDate, setEndDate] = useState<Date | null>(null);
  const [promoHeadlines, setPromoHeadlines] =
    useState<{
      headline_en?: string | null;
      headline_de?: string | null;
    } | null>(null);

  const isAuthorized = Boolean(userData?.user?.id || authorizationToken);

  const promoTitle = useMemo(() => {
    if (!promoHeadlines) return null;

    if (locale === 'de') {
      return promoHeadlines.headline_de ?? promoHeadlines.headline_en ?? null;
    }

    return promoHeadlines.headline_en ?? promoHeadlines.headline_de ?? null;
  }, [locale, promoHeadlines]);

  const requestTimerDate = useCallback(async () => {
    try {
      const {responseBody} = await getTimerDate();

      setEndDate(responseBody.offer_end_at);
      setPromoHeadlines({
        headline_en: responseBody.headline_en,
        headline_de: responseBody.headline_de,
      });
    } catch (e) {
      console.warn(e);
    }
  }, []);

  useEffect(() => {
    if (isAuthorized) {
      return;
    }

    requestTimerDate();
  }, [isAuthorized, requestTimerDate]);

  if (isAuthorized) {
    return null;
  }

  return endDate ? (
    <Timer
      endDate={endDate}
      headline={promoTitle}
      isRounded={isRounded}
      onClose={onClose}
    />
  ) : null;
};

export default TimerContainer;
